import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { message } from "antd";
import {
  useGetPaymentAccountsQuery,
  useSubmitDepositMutation,
} from "../../../store/service/paymentService";
import ClientPaymentRequestTables from "./ClientPaymentRequestTables";
import "./Client.scss";

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    message.success(`${label} copied`);
  } catch {
    message.error("Copy failed");
  }
};

const ClientDeposit = () => {
  const { data, isLoading } = useGetPaymentAccountsQuery();
  const accounts = data?.data || [];
  const bankAccounts = accounts.filter((a) => a.type === "bank");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!selectedId && bankAccounts.length) {
      setSelectedId(bankAccounts[0].id);
    }
  }, [bankAccounts, selectedId]);

  const active = bankAccounts.find((a) => a.id === selectedId) || bankAccounts[0] || null;
  const minAmount = Number(active?.minAmount || 100);
  const maxAmount = Number(active?.maxAmount || 100000);

  const [utr, setUtr] = useState("");
  const [amount, setAmount] = useState("");
  const [proof, setProof] = useState(null);
  const [agree, setAgree] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitDeposit, { isLoading: submitting }] = useSubmitDepositMutation();

  const mark = (field) => setTouched((t) => ({ ...t, [field]: true }));

  const errors = useMemo(() => {
    const e = {};
    const utrVal = String(utr || "").trim();
    if (!utrVal) e.utr = "UTR / transaction reference is required";
    else if (utrVal.length < 6) e.utr = "UTR must be at least 6 characters";
    else if (utrVal.length > 20) e.utr = "UTR must be at most 20 characters";
    else if (!/^[A-Za-z0-9]+$/.test(utrVal)) e.utr = "UTR can only contain letters and numbers";

    if (!proof) e.proof = "Upload a clear payment proof image";
    else if (proof.size > 10 * 1024 * 1024) e.proof = "Image must be under 10 MB";

    const amt = Number(amount);
    if (!amount || Number.isNaN(amt)) e.amount = "Enter deposit amount";
    else if (amt < minAmount) e.amount = `Minimum deposit is ${minAmount}`;
    else if (amt > maxAmount) e.amount = `Maximum deposit is ${maxAmount}`;

    if (!agree) e.agree = "Please accept the payment policy";
    if (!active) e.account = "No bank account available";

    return e;
  }, [utr, proof, amount, agree, active, minAmount, maxAmount]);

  const show = (field) => touched[field] && errors[field];

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched({ utr: true, proof: true, amount: true, agree: true });
    if (Object.keys(errors).length) {
      message.error(Object.values(errors)[0]);
      return;
    }

    const formData = new FormData();
    formData.append("payment_account_id", String(active.id));
    formData.append("amount", amount);
    formData.append("utr", utr.trim());
    formData.append("proof", proof);

    try {
      const res = await submitDeposit(formData).unwrap();
      message.success(res.message || "Submitted");
      setUtr("");
      setAmount("");
      setProof(null);
      setAgree(false);
      setTouched({});
    } catch (err) {
      message.error(err?.data?.message || "Submit failed");
    }
  };

  return (
    <div className="lotus-pay-page">
      <Link to="/play" className="lotus-back-btn">
        ← Back
      </Link>

      <div className="lotus-section-title">Deposit</div>

      {isLoading ? (
        <p className="lotus-pay-loading">Loading payment accounts…</p>
      ) : (
        <>
          <div className="lotus-pay-tabs">
            {accounts.map((acc) => (
              <button
                key={acc.id}
                type="button"
                className={`lotus-pay-tab ${
                  acc.type === "bank" &&
                  (active?.id === acc.id || (!active && acc.id === bankAccounts[0]?.id))
                    ? "active"
                    : ""
                }`}
                onClick={() => acc.type === "bank" && setSelectedId(acc.id)}>
                <span className="pe-tab-icon">{acc.type === "whatsapp" ? "WA" : "BANK"}</span>
                <span>{acc.label}</span>
              </button>
            ))}
          </div>

          {active ? (
            <div className="lotus-bank-card">
              <h3>Transfer to this account</h3>
              <div className="lotus-bank-row">
                <span>Bank</span>
                <strong>{active.bankName || "—"}</strong>
              </div>
              <div className="lotus-bank-row copyable">
                <span>A/C No</span>
                <strong>{active.accountNumber}</strong>
                <button type="button" onClick={() => copyText(active.accountNumber, "A/C No")}>
                  Copy
                </button>
              </div>
              <div className="lotus-bank-row copyable">
                <span>IFSC</span>
                <strong>{active.ifscCode}</strong>
                <button type="button" onClick={() => copyText(active.ifscCode, "IFSC")}>
                  Copy
                </button>
              </div>
              <div className="lotus-bank-row copyable">
                <span>Name</span>
                <strong>{active.accountName}</strong>
                <button type="button" onClick={() => copyText(active.accountName, "Name")}>
                  Copy
                </button>
              </div>
              <div className="lotus-bank-row">
                <span>Min</span>
                <strong>{active.minAmount}</strong>
              </div>
              <div className="lotus-bank-row">
                <span>Max</span>
                <strong>{active.maxAmount}</strong>
              </div>
            </div>
          ) : (
            <p className="lotus-pay-loading">No bank account configured yet. Contact support.</p>
          )}

          {active?.instructions ? (
            <div className="lotus-pay-note">{active.instructions}</div>
          ) : null}

          <form className="lotus-pay-form" onSubmit={onSubmit} noValidate>
            <label className={show("utr") ? "has-error" : ""}>
              UTR / Transaction reference *
              <input
                type="text"
                placeholder="6–20 characters"
                value={utr}
                onChange={(e) => setUtr(e.target.value.replace(/\s/g, "").slice(0, 20))}
                onBlur={() => mark("utr")}
                maxLength={20}
                required
              />
              {show("utr") ? <span className="field-error">{errors.utr}</span> : null}
            </label>

            <label className={show("proof") ? "has-error" : ""}>
              Payment proof (image) *
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  setProof(e.target.files?.[0] || null);
                  mark("proof");
                }}
                onBlur={() => mark("proof")}
                required
              />
              {show("proof") ? <span className="field-error">{errors.proof}</span> : null}
            </label>

            <label className={show("amount") ? "has-error" : ""}>
              Amount *
              <input
                type="number"
                placeholder={`Min ${minAmount} · Max ${maxAmount}`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onBlur={() => mark("amount")}
                min={minAmount}
                max={maxAmount}
                step="0.01"
                required
              />
              {show("amount") ? <span className="field-error">{errors.amount}</span> : null}
            </label>

            <label className={`lotus-pay-check ${show("agree") ? "has-error" : ""}`}>
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => {
                  setAgree(e.target.checked);
                  mark("agree");
                }}
              />
              <span>
                I have read and agree with the payment and withdrawal policy.
                {show("agree") ? <span className="field-error block">{errors.agree}</span> : null}
              </span>
            </label>

            <button
              type="submit"
              className="lotus-pay-submit"
              disabled={submitting || !active || Object.keys(errors).length > 0}>
              {submitting ? "Submitting…" : "Submit deposit"}
            </button>
          </form>

          <div className="lotus-pay-rules">
            <p>1. Deposit only to the account shown above for fastest credit.</p>
            <p>2. Upload a clear UTR and payment screenshot.</p>
            <p>3. Admin will verify and credit your wallet.</p>
          </div>

          <ClientPaymentRequestTables filter="all" />
        </>
      )}
    </div>
  );
};

export default ClientDeposit;
